import { supabase } from "@/integrations/supabase/client";
import { prefersNativeHls } from "@/services/playback/hlsConfig";

/**
 * Régie publicitaire interne, côté membre.
 *
 * - Une file de publicités par emplacement ('home' ou `hashtag:${tagId}`),
 *   consommée dans l'ordre reçu du serveur (premier entré, premier sorti).
 * - takeAd est SYNCHRONE : jamais d'attente réseau dans le chemin d'affichage.
 * - La cadence (vidéos depuis la dernière publicité, seuil) vit ici, pour toute
 *   la session, par emplacement ('home' et 'hashtag').
 * Tout le classement et les plafonds sont décidés côté serveur.
 */

export type AdPlacement = "home" | "hashtag";
export type AdEventKind = "impression" | "completion" | "click";

export interface ServedAd {
  campaignId: string;
  kind: "sponsor" | "house";
  mediaType: "video" | "image";
  mediaUrl: string;
  /** Playlist HLS Bunny quand l'encodage est prêt, sinon null. */
  hlsUrl: string | null;
  mediaExpiresAt: string;
  headline: string | null;
  advertiserName: string;
  ctaKind: "none" | "profile" | "product" | "external";
  ctaLabel: string | null;
  ctaProfileUsername: string | null;
  ctaProductId: string | null;
  ctaUrl: string | null;
  durationSeconds: number;
  /** Attribué à l'insertion : identifie l'emplacement pour le comptage unique. */
  slotId: string;
  placement: AdPlacement;
  tagId?: string;
}

export interface AdSettings {
  enabled: boolean;
  min_gap: number;
  max_gap: number;
  duration_seconds: number;
  creative_max_size_mb: number;
  rotation_mode: "weighted" | "random" | "ordered";
  gap_mode: "random" | "fixed";
  fixed_gap: number;
}

/** Valeurs de repli si la lecture du réglage échoue (même cadence qu'avant). */
export const DEFAULT_AD_SETTINGS: AdSettings = {
  enabled: true,
  min_gap: 10,
  max_gap: 20,
  duration_seconds: 10,
  creative_max_size_mb: 20,
  rotation_mode: "weighted",
  gap_mode: "random",
  fixed_gap: 12,
};

const REFILL_BELOW = 2;
const BATCH_SIZE = 3;
const EXPIRY_MARGIN_MS = 5 * 60_000;

type ServerAd = Omit<ServedAd, "slotId" | "placement" | "tagId">;

const queues = new Map<string, ServerAd[]>();
const inflight = new Map<string, Promise<void>>();
const lastServed = new Map<string, string>();
const cadence = new Map<AdPlacement, { since: number; threshold: number }>();
let settingsCache: AdSettings | null = null;
let settingsPromise: Promise<AdSettings> | null = null;

const queueKey = (placement: AdPlacement, tagId?: string) =>
  placement === "hashtag" && tagId ? `hashtag:${tagId}` : "home";

/** Normalise le réglage « ads » lu en base (jamais de plantage sur un réglage mal formé). */
export function normalizeAdSettings(raw: unknown): AdSettings {
  const v = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const int = (x: unknown, fallback: number, min = 1) => {
    const n = Number(x);
    return Number.isFinite(n) && n >= min ? Math.round(n) : fallback;
  };
  const min = int(v.min_gap, DEFAULT_AD_SETTINGS.min_gap);
  const max = Math.max(min, int(v.max_gap, DEFAULT_AD_SETTINGS.max_gap));
  return {
    enabled: v.enabled !== false,
    min_gap: min,
    max_gap: max,
    duration_seconds: int(v.duration_seconds, DEFAULT_AD_SETTINGS.duration_seconds),
    creative_max_size_mb: int(v.creative_max_size_mb, DEFAULT_AD_SETTINGS.creative_max_size_mb),
    rotation_mode: v.rotation_mode === "random" || v.rotation_mode === "ordered" ? v.rotation_mode : "weighted",
    gap_mode: v.gap_mode === "fixed" ? "fixed" : "random",
    fixed_gap: int(v.fixed_gap, DEFAULT_AD_SETTINGS.fixed_gap),
  };
}

/** Lecture unique, mise en cache, du réglage « ads ». */
export function loadAdSettings(): Promise<AdSettings> {
  if (settingsCache) return Promise.resolve(settingsCache);
  if (!settingsPromise) {
    settingsPromise = (async () => {
      const { data, error } = await supabase.from("app_settings").select("value").eq("key", "ads").maybeSingle();
      if (error) console.error("Lecture du réglage publicitaire impossible:", error);
      settingsCache = error ? DEFAULT_AD_SETTINGS : normalizeAdSettings(data?.value);
      return settingsCache;
    })().finally(() => {
      settingsPromise = null;
    });
  }
  return settingsPromise;
}

const currentSettings = () => settingsCache ?? DEFAULT_AD_SETTINGS;

function drawThreshold(): number {
  const s = currentSettings();
  if (s.gap_mode === "fixed") return s.fixed_gap;
  return s.min_gap + Math.floor(Math.random() * (s.max_gap - s.min_gap + 1));
}

function cadenceOf(placement: AdPlacement) {
  let c = cadence.get(placement);
  if (!c) {
    c = { since: 0, threshold: drawThreshold() };
    cadence.set(placement, c);
  }
  return c;
}

/** Redemande des publicités en tâche de fond (un seul appel à la fois par file). */
function refill(placement: AdPlacement, tagId?: string): Promise<void> {
  const key = queueKey(placement, tagId);
  const pending = inflight.get(key);
  if (pending) return pending;

  const run = (async () => {
    const settings = await loadAdSettings();
    if (!settings.enabled) return;
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return;

    const queue = queues.get(key) ?? [];
    // Queue de file d'abord : l'ordre voulu continue sans revenir en arrière.
    const lastCampaignId = queue[queue.length - 1]?.campaignId ?? lastServed.get(key);
    const { data, error } = await supabase.functions.invoke("get-feed-ads", {
      body: {
        count: BATCH_SIZE,
        placement,
        ...(placement === "hashtag" && tagId ? { tagId } : {}),
        ...(lastCampaignId ? { lastCampaignId } : {}),
      },
    });
    if (error) {
      console.error("Chargement des publicités impossible:", error);
      return;
    }
    const ads = ((data as { ads?: ServerAd[] } | null)?.ads ?? []).filter(Boolean);
    queues.set(key, [...(queues.get(key) ?? []), ...ads]);
  })()
    .catch((error) => console.error("Chargement des publicités impossible:", error))
    .finally(() => inflight.delete(key));

  inflight.set(key, run);
  return run;
}

/** Remplit la file d'un emplacement. À appeler APRÈS l'affichage de la première vidéo. */
export function primeAds(placement: AdPlacement, tagId?: string): Promise<void> {
  return refill(placement, tagId);
}

/** Sert une publicité déjà en file, ou null (aucune attente réseau). */
export function takeAd(placement: AdPlacement, tagId?: string): ServedAd | null {
  if (!currentSettings().enabled) return null;
  const key = queueKey(placement, tagId);
  const queue = queues.get(key) ?? [];
  const limit = Date.now() + EXPIRY_MARGIN_MS;
  // Publicités périmées jetées : une adresse signée vit une heure.
  while (queue.length && new Date(queue[0].mediaExpiresAt).getTime() <= limit) queue.shift();
  const next = queue.shift() ?? null;
  queues.set(key, queue);
  if (queue.length < REFILL_BELOW) void refill(placement, tagId);
  if (!next) return null;
  lastServed.set(key, next.campaignId);
  return { ...next, slotId: crypto.randomUUID(), placement, tagId };
}

/**
 * Compte une vidéo et renvoie une publicité si la cadence est atteinte.
 * File vide : rien n'est inséré et le compteur n'est PAS remis à zéro.
 */
export function nextAdAfterVideo(placement: AdPlacement, tagId?: string): ServedAd | null {
  const c = cadenceOf(placement);
  c.since += 1;
  return takeDueAd(placement, tagId);
}

/** Publicité due (cadence déjà atteinte), sans compter de vidéo supplémentaire. */
export function takeDueAd(placement: AdPlacement, tagId?: string): ServedAd | null {
  const c = cadenceOf(placement);
  if (c.since < c.threshold) return null;
  const ad = takeAd(placement, tagId);
  if (ad) {
    c.since = 0;
    c.threshold = drawThreshold();
  }
  return ad;
}

/** Compte une vidéo sans possibilité d'insertion (ajout temps réel en tête de fil). */
export function countVideoOnly(placement: AdPlacement) {
  cadenceOf(placement).since += 1;
}

/** Télécharge la création en tâche de fond, comme les vidéos voisines du lecteur. */
const prefetched = new Set<string>();
/** Adresse de lecture : HLS seulement si le navigateur le lit nativement (iPhone). */
export function adPlaybackUrl(ad: ServedAd): string {
  if (ad.mediaType === "video" && ad.hlsUrl && prefersNativeHls()) return ad.hlsUrl;
  return ad.mediaUrl;
}

export function prefetchAd(ad: ServedAd) {
  if (prefetched.has(ad.slotId)) return;
  prefetched.add(ad.slotId);
  if (ad.mediaType === "image") {
    const img = new Image();
    img.decoding = "async";
    img.src = ad.mediaUrl;
    return;
  }
  const video = document.createElement("video");
  video.muted = true;
  video.preload = "metadata";
  video.src = adPlaybackUrl(ad);
}

/** Envoie une métrique ; un échec ne remonte jamais au membre. */
export function logAdEvent(ad: ServedAd, kind: AdEventKind) {
  supabase.functions
    .invoke("log-ad-event", {
      body: {
        campaignId: ad.campaignId,
        slotId: ad.slotId,
        kind,
        placement: ad.placement,
        ...(ad.tagId ? { tagId: ad.tagId } : {}),
      },
    })
    .catch((error) => console.error("Comptage publicitaire impossible:", error));
}

/** Vide tout (files, compteurs, réglage) : aucune donnée d'un ancien compte ne survit. */
export function resetAds() {
  queues.clear();
  inflight.clear();
  lastServed.clear();
  cadence.clear();
  prefetched.clear();
  settingsCache = null;
}

supabase.auth.onAuthStateChange((event) => {
  if (event === "SIGNED_OUT") resetAds();
});

/** Élément de liste « publicité » inséré dans un fil. */
export interface AdItem {
  id: string;
  isAd: true;
  ad: ServedAd;
}

export const makeAdItem = (ad: ServedAd): AdItem => ({ id: `ad-${ad.slotId}`, isAd: true, ad });

/** Insère les publicités dans une page de vidéos selon la cadence de session. */
export function interleaveAds<T>(videos: T[], placement: AdPlacement, tagId?: string): (T | AdItem)[] {
  const items: (T | AdItem)[] = [];
  for (const video of videos) {
    items.push(video);
    const ad = nextAdAfterVideo(placement, tagId);
    if (ad) items.push(makeAdItem(ad));
  }
  return items;
}

export const isAdItem = (item: unknown): item is AdItem =>
  !!item && typeof item === "object" && "isAd" in item && (item as AdItem).isAd === true;
